import React, { useState, useRef, useEffect } from "react";
import { MdAccessTime, MdKeyboardArrowDown } from "react-icons/md";
import FormField, { cx, getControlClassName } from "components/fields/FormField";

const TimePicker = ({ label, value, onChange, placeholder, id, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState(12);
  const [selectedMinute, setSelectedMinute] = useState(0);
  const [period, setPeriod] = useState("AM");
  const dropdownRef = useRef(null);

  // Parse the incoming time value (12-hour format with AM/PM)
  useEffect(() => {
    if (value) {
      const parts = value.split(" ");
      const time = parts[0];
      const providedPeriod = parts[1]; // Extract the period from the value string

      let [hours, minutes] = time.split(":").map(Number);

      // If period is provided in the value string, use it directly (12-hour format)
      // Otherwise, assume 24-hour format and calculate AM/PM from hours
      let newPeriod;
      let displayHour;

      if (providedPeriod) {
        // Value already includes AM/PM, so hours are in 12-hour format
        newPeriod = providedPeriod;
        displayHour = hours;
      } else {
        // No AM/PM provided, assume 24-hour format
        newPeriod = hours >= 12 ? "PM" : "AM";
        displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      }

      setSelectedHour(displayHour);
      setSelectedMinute(minutes);
      setPeriod(newPeriod);
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatTo12Hour = (hour, minute, period) => {
    const h = String(hour).padStart(2, "0");
    const m = String(minute).padStart(2, "0");
    return `${h}:${m} ${period}`;
  };

  const handleTimeSelect = (hour, minute, newPeriod) => {
    setSelectedHour(hour);
    setSelectedMinute(minute);
    setPeriod(newPeriod);

    const formattedTime = formatTo12Hour(hour, minute, newPeriod);
    onChange({ target: { value: formattedTime } });
  };

  const displayValue = value
    ? formatTo12Hour(selectedHour, selectedMinute, period)
    : "";

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = [0, 15, 30, 45];

  const optionClass = (selected) =>
    cx(
      "w-full px-4 py-2 text-left text-sm transition-colors focus-ring",
      selected
        ? "bg-primary font-medium text-primary-fg"
        : "text-fg hover:bg-surface-subtle"
    );
  const columnHeaderClass =
    "border-b border-line bg-surface-subtle px-3 py-2 text-xs font-semibold text-fg-muted";
  const quickSelectClass =
    "flex-1 rounded-control border border-line bg-surface px-3 py-1.5 text-xs font-medium text-fg transition-colors hover:bg-surface-subtle focus-ring";

  return (
    <FormField id={id} label={label} error={error} className="flex flex-1 flex-col">
      {(controlProps) => (
        <div
          className="relative"
          ref={dropdownRef}
          onKeyDown={(e) => {
            if (e.key === "Escape" && isOpen) setIsOpen(false);
          }}
        >
          <button
            type="button"
            {...controlProps}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            onClick={() => setIsOpen(!isOpen)}
            className={getControlClassName({
              invalid: controlProps["aria-invalid"],
              className: "flex items-center gap-2 text-left",
            })}
          >
            <MdAccessTime aria-hidden="true" className="h-5 w-5 shrink-0 text-fg-muted" />
            <span className={cx("flex-1 truncate", !displayValue && "text-fg-muted")}>
              {displayValue || placeholder || "Select time"}
            </span>
            <MdKeyboardArrowDown
              aria-hidden="true"
              className={cx(
                "h-5 w-5 shrink-0 text-fg-muted transition-transform",
                isOpen && "rotate-180"
              )}
            />
          </button>

          {isOpen && (
            <div
              role="dialog"
              aria-label="Choose time"
              className="absolute z-50 mt-2 w-full overflow-hidden rounded-control border border-line bg-surface shadow-overlay"
            >
              <div className="flex">
                <div role="group" aria-label="Hour" className="flex-1 border-r border-line">
                  <div aria-hidden="true" className={columnHeaderClass}>Hour</div>
                  <div className="time-picker-scroll max-h-48 overflow-y-auto">
                    {hours.map((hour) => (
                      <button
                        key={hour}
                        type="button"
                        aria-pressed={selectedHour === hour}
                        onClick={() => handleTimeSelect(hour, selectedMinute, period)}
                        className={optionClass(selectedHour === hour)}
                      >
                        {String(hour).padStart(2, "0")}
                      </button>
                    ))}
                  </div>
                </div>

                <div role="group" aria-label="Minute" className="flex-1 border-r border-line">
                  <div aria-hidden="true" className={columnHeaderClass}>Minute</div>
                  <div className="time-picker-scroll max-h-48 overflow-y-auto">
                    {minutes.map((minute) => (
                      <button
                        key={minute}
                        type="button"
                        aria-pressed={selectedMinute === minute}
                        onClick={() => handleTimeSelect(selectedHour, minute, period)}
                        className={optionClass(selectedMinute === minute)}
                      >
                        {String(minute).padStart(2, "0")}
                      </button>
                    ))}
                  </div>
                </div>

                <div role="group" aria-label="Period" className="w-20">
                  <div aria-hidden="true" className={columnHeaderClass}>Period</div>
                  <div>
                    {["AM", "PM"].map((p) => (
                      <button
                        key={p}
                        type="button"
                        aria-pressed={period === p}
                        onClick={() => handleTimeSelect(selectedHour, selectedMinute, p)}
                        className={optionClass(period === p)}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-2 border-t border-line bg-surface-subtle p-2">
                {[
                  [9, 0, "AM", "9:00 AM"],
                  [12, 0, "PM", "12:00 PM"],
                  [6, 0, "PM", "6:00 PM"],
                ].map(([hour, minute, p, text]) => (
                  <button
                    key={text}
                    type="button"
                    onClick={() => {
                      handleTimeSelect(hour, minute, p);
                      setIsOpen(false);
                    }}
                    className={quickSelectClass}
                  >
                    {text}
                  </button>
                ))}
              </div>
            </div>
          )}

          <style
            dangerouslySetInnerHTML={{
              __html: `
                .time-picker-scroll::-webkit-scrollbar { width: 6px; }
                .time-picker-scroll::-webkit-scrollbar-track { background: transparent; }
                .time-picker-scroll::-webkit-scrollbar-thumb { background: rgb(var(--color-line)); border-radius: 3px; }
                .time-picker-scroll::-webkit-scrollbar-thumb:hover { background: rgb(var(--color-fg-muted)); }
              `,
            }}
          />
        </div>
      )}
    </FormField>
  );
};

export default TimePicker;
